import { useState, useEffect, useRef } from 'react';
import { useNotification } from '../../shared/contexts/NotificationContext';
import { getApiEndpoint } from '../../shared/utils/api-url';

interface Order {
  id: number;
  order_code: string;
  customer_name: string;
  total: number;
  status: string;
}

// Intervalo de verificação (em milissegundos) - 10 segundos
const CHECK_INTERVAL = 10000;
// Timeout para confirmação do pedido (5 minutos em segundos)
const CONFIRMATION_TIMEOUT_SECONDS = 300;

function OrderConfirmationBanner() {
  const { success, error, confirm: confirmDialog } = useNotification();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  // Verificar se há pedido pendente no localStorage (verificação rápida)
  const hasPendingOrderInStorage = (): boolean => {
    const pendingOrderCode = localStorage.getItem('pending_order_code');
    const pendingOrderId = localStorage.getItem('pending_order_id');
    return !!(pendingOrderCode && pendingOrderId);
  };

  // Calcular tempo restante baseado no timestamp de criação
  const calculateRemainingTime = (createdAt: string | number): number => {
    const createdTime = typeof createdAt === 'string' ? new Date(createdAt).getTime() : createdAt;
    const now = Date.now();
    const elapsed = Math.floor((now - createdTime) / 1000); // segundos decorridos
    const remaining = CONFIRMATION_TIMEOUT_SECONDS - elapsed;
    return Math.max(0, remaining); // Retorna 0 se já expirou
  };

  // Verificar pedido pendente via API
  const checkPendingOrder = async (immediate = false): Promise<boolean> => {
    // Evitar múltiplas verificações simultâneas
    if (isCheckingRef.current && !immediate) {
      return false;
    }

    // Verificação rápida no localStorage primeiro
    if (!hasPendingOrderInStorage()) {
      return false;
    }

    const pendingOrderCode = localStorage.getItem('pending_order_code');
    const pendingOrderId = localStorage.getItem('pending_order_id');
    const orderCreatedAt = localStorage.getItem('pending_order_created_at');
    
    if (!pendingOrderCode || !pendingOrderId) {
      return false;
    }

    // Verificar se o timeout já expirou baseado no timestamp
    if (orderCreatedAt) {
      const remaining = calculateRemainingTime(orderCreatedAt);
      if (remaining <= 0) {
        // Timeout expirado, limpar localStorage
        localStorage.removeItem('pending_order_code');
        localStorage.removeItem('pending_order_id');
        localStorage.removeItem('pending_order_created_at');
        return false;
      }
    }

    // Se já temos um pedido sendo exibido e não é verificação imediata, não verificar
    if (order && !immediate) {
      return true;
    }

    isCheckingRef.current = true;

    try {
      const response = await fetch(getApiEndpoint(`/orders/code/${pendingOrderCode}`));
      
      if (!response.ok) {
        // Pedido não encontrado ou já foi processado
        localStorage.removeItem('pending_order_code');
        localStorage.removeItem('pending_order_id');
        localStorage.removeItem('pending_order_created_at');
        isCheckingRef.current = false;
        return false;
      }

      // Verificar se a resposta é JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Resposta não é JSON:', text.substring(0, 100));
        isCheckingRef.current = false;
        return false;
      }

      const orderData = await response.json();
      
      // Verificar se o pedido ainda está pendente
      if (orderData.status === 'pending') {
        // Calcular tempo restante baseado na data de criação do pedido
        const orderCreatedAtTime = orderData.created_at || orderCreatedAt || Date.now();
        const remaining = calculateRemainingTime(orderCreatedAtTime);
        
        if (remaining > 0) {
          setOrder(orderData);
          setCountdown(remaining);
          startCountdown();
          isCheckingRef.current = false;
          return true;
        } else {
          // Timeout expirado
          localStorage.removeItem('pending_order_code');
          localStorage.removeItem('pending_order_id');
          localStorage.removeItem('pending_order_created_at');
          isCheckingRef.current = false;
          return false;
        }
      } else {
        // Pedido já foi confirmado ou processado
        localStorage.removeItem('pending_order_code');
        localStorage.removeItem('pending_order_id');
        localStorage.removeItem('pending_order_created_at');
        isCheckingRef.current = false;
        return false;
      }
    } catch (err) {
      console.error('Erro ao verificar pedido:', err);
      isCheckingRef.current = false;
      return false;
    }
  };

  // Iniciar contador regressivo
  const startCountdown = () => {
    // Limpar contador anterior se existir
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          closeBanner();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Parar contador regressivo
  const stopCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  // Iniciar polling de verificação
  const startPolling = () => {
    // Limpar intervalo anterior se existir
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Verificar imediatamente (primeira vez)
    checkPendingOrder(true);

    // Configurar intervalo de verificação (só roda quando não há pedido sendo exibido)
    intervalRef.current = setInterval(() => {
      // Só verificar se não há pedido sendo exibido
      if (!order && hasPendingOrderInStorage()) {
        checkPendingOrder(false);
      }
    }, CHECK_INTERVAL);
  };

  // Parar polling
  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Reiniciar polling (após confirmar/cancelar)
  const resetPolling = () => {
    stopPolling();
    // Pequeno delay antes de reiniciar para evitar verificação imediata
    setTimeout(() => {
      startPolling();
    }, 1000);
  };

  useEffect(() => {
    // Iniciar polling quando o componente montar (verificação imediata)
    startPolling();

    // Limpar intervalos ao desmontar
    return () => {
      stopPolling();
      stopCountdown();
    };
  }, []);

  // Parar polling quando um pedido aparecer, reiniciar quando desaparecer
  useEffect(() => {
    if (order) {
      stopPolling(); // Para de verificar quando há pedido sendo exibido
    } else {
      // Quando o pedido desaparece, reinicia o polling
      startPolling();
    }
  }, [order]);

  const confirmOrder = async () => {
    if (!order) return;
    
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/orders/${order.id}/confirm`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorData: any = { error: 'Erro ao confirmar pedido' };
        if (contentType && contentType.includes('application/json')) {
          try {
            errorData = await response.json();
          } catch (e) {
            console.error('Erro ao parsear resposta de erro:', e);
          }
        }
        throw new Error(errorData.error || 'Erro ao confirmar pedido');
      }

      success('Pedido confirmado com sucesso!');
      localStorage.removeItem('pending_order_code');
      localStorage.removeItem('pending_order_id');
      localStorage.removeItem('pending_order_created_at');
      stopCountdown();
      closeBanner();
      
      // Recarregar a página após 1 segundo para atualizar o estado
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err: any) {
      console.error('Erro ao confirmar pedido:', err);
      error(err.message || 'Erro ao confirmar pedido');
      
      // Se o pedido não foi encontrado, remover do localStorage
      if (err.message.includes('não encontrado') || err.message.includes('404')) {
        localStorage.removeItem('pending_order_code');
        localStorage.removeItem('pending_order_id');
        closeBanner();
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async () => {
    if (!order) return;
    
    const confirmed = await confirmDialog({
      title: 'Cancelar Pedido',
      message: 'Tem certeza que deseja cancelar este pedido?',
      confirmText: 'Sim, Cancelar',
      cancelText: 'Não',
    });
    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/orders/${order.id}/cancel`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorData: any = { error: 'Erro ao cancelar pedido' };
        if (contentType && contentType.includes('application/json')) {
          try {
            errorData = await response.json();
          } catch (e) {
            console.error('Erro ao parsear resposta de erro:', e);
          }
        }
        throw new Error(errorData.error || 'Erro ao cancelar pedido');
      }

      success('Pedido cancelado com sucesso');
      localStorage.removeItem('pending_order_code');
      localStorage.removeItem('pending_order_id');
      localStorage.removeItem('pending_order_created_at');
      stopCountdown();
      closeBanner();
    } catch (err: any) {
      console.error('Erro ao cancelar pedido:', err);
      error(err.message || 'Erro ao cancelar pedido');
    } finally {
      setLoading(false);
    }
  };

  const closeBanner = () => {
    setOrder(null);
    setCountdown(null);
    stopCountdown();
    // Não reiniciar polling aqui - o useEffect vai cuidar disso
    // O polling vai continuar verificando e o banner vai reaparecer se ainda houver tempo
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 'R$ 0,00';
    return `R$ ${numPrice.toFixed(2).replace('.', ',')}`;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!order) {
    return null;
  }

  return (
    <div className="order-confirmation-banner">
      <div className="banner-content">
        <div className="banner-icon">
          <i className="fas fa-shopping-bag"></i>
        </div>
        <div className="banner-info">
          <h3 className="banner-title">Pedido Criado!</h3>
          <p className="banner-message">
            Pedido <strong>{order.order_code}</strong> criado com sucesso.
            <br />
            Valor: <strong>{formatPrice(order.total)}</strong>
          </p>
          {countdown !== null && countdown > 0 && (
            <p className="banner-countdown">
              Confirme em <strong>{formatTime(countdown)}</strong> para processar
            </p>
          )}
        </div>
        <div className="banner-actions">
          <button
            type="button"
            className="banner-btn banner-btn-confirm"
            onClick={confirmOrder}
            disabled={loading}
          >
            <i className="fas fa-check"></i>
            Confirmar
          </button>
          <button
            type="button"
            className="banner-btn banner-btn-cancel"
            onClick={cancelOrder}
            disabled={loading}
          >
            <i className="fas fa-times"></i>
            Cancelar
          </button>
          <button
            type="button"
            className="banner-btn banner-btn-close"
            onClick={closeBanner}
            disabled={loading}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderConfirmationBanner;

